import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { IoExitOutline } from 'react-icons/io5';
import { RiGroupLine } from 'react-icons/ri';

import { useRoomContext, useWhiteboardContext } from '@src/contexts';
import { useExitRoomStore, useRoomActionsStore } from '@src/stores';

import style from './leaveMeetingPopper.module.scss';

export type TMenuItemConfig = {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  className?: string;
};

export const useLeaveMeetingPopper = (pipWindow?: Window) => {
  const [isActive, setIsActive] = useState(false);

  const { setIsCallEnded, isHostOrCohost } = useRoomContext();

  const {
    exitRoomSettings: { onExitClick },
  } = useRoomActionsStore();

  const { handleCloseWhiteboard } = useWhiteboardContext();
  const { setExitRoomState } = useExitRoomStore();

  const handleExitClick = useCallback(
    (endForAll?: boolean) => {
      setIsActive(false);
      setIsCallEnded(true);

      // Close PiP window if it exists
      if (pipWindow && !pipWindow.closed) {
        pipWindow.close();
      }

      setTimeout(() => {
        endForAll ? onExitClick(endForAll) : onExitClick();
      }, 0);
    },
    [onExitClick, setIsCallEnded, pipWindow]
  );

  const closeModal = useCallback(() => {
    setExitRoomState({ isOpenModal: false });
  }, [setExitRoomState]);

  const handleLeaveForMe = useCallback(() => {
    handleCloseWhiteboard(true);
    closeModal();
    handleExitClick();
  }, [handleCloseWhiteboard, closeModal, handleExitClick]);

  const handleEndForAll = useCallback(() => {
    closeModal();
    handleExitClick(true);
  }, [closeModal, handleExitClick]);

  const menuItems: TMenuItemConfig[] = useMemo(
    () =>
      [
        {
          id: 'leaveMeeting',
          label: 'End meeting for me',
          icon: <IoExitOutline className={style.iconDefault} />,
          onClick: handleLeaveForMe,
          className: style.itemDefault,
        },
        isHostOrCohost
          ? {
              id: 'endForAll',
              label: 'End meeting for all',
              icon: <RiGroupLine className={style.iconDanger} />,
              onClick: handleEndForAll,
              className: style.itemDanger,
            }
          : null,
      ].filter(Boolean) as TMenuItemConfig[],
    [handleLeaveForMe, handleEndForAll, isHostOrCohost]
  );

  return {
    isActive,
    menuItems,
    leaveForMe: handleLeaveForMe,
    setIsActive,
  };
};
